<template>
  <div class="dashboard-container">
    <div class="dashboard-header">
      <mc-date-picker
        ref="periodPicker"
        v-model="periodDate"
        :placeholder="$t('global.action.selectDate')"
        class="dashboard-period-picker"
        format="DD MMM YYYY"
        value-type="YYYY-MM-DD"
        range-separator=" - "
        range
        :draft="true"
        :confirm="true"
        :confirm-text="$t('global.action.apply')"
        :lang="datePickerLang"
        :shortcuts="shortcuts"
        :clearable="false"
        :disabled-date="disabledDate"
        @input="refetchCDMData(false)"
      >
        <template #footer>
          <b-button class="mx-2" variant="outline-primary" @click="closeCalendar()">
            {{ $t('global.action.cancel') }}
          </b-button>
        </template>
      </mc-date-picker>
    </div>
    <div class="dashboard-alert-section">
      <div>
        <h5>Device CDM</h5>
        <div class="dashboard-widgets">
          <template v-if="isFirstLoad && isLoading">
            <AlertWidget
              v-for="n in 6"
              :key="'device-skeleton-' + n"
              :loading="true"
              :isFullLoading="true"
            />
          </template>
          <template v-else>
            <AlertWidget
              v-for="data in deviceCdmAlerts"
              :key="data.alertCode"
              :title="data.name"
              :counter="data.count"
              :loading="isDatePickerLoading"
              :isFullLoading="false"
            />
          </template>
        </div>
      </div>
      <div>
        <h5>Camera Warning</h5>
        <div class="dashboard-widgets">
          <template v-if="isFirstLoad && isLoading">
            <AlertWidget
              v-for="n in 4"
              :key="'camera-skeleton-' + n"
              :loading="true"
              :isFullLoading="true"
            />
          </template>
          <template v-else>
            <AlertWidget
              v-for="data in cameraWarningAlerts"
              :key="data.alertCode"
              :title="data.name"
              :counter="data.count"
              :loading="isDatePickerLoading"
              :isFullLoading="false"
            />
          </template>
        </div>
      </div>
    </div>
    <div>
      <h5>{{ $t('cdmSecurityMonitoring.dashboard.table.title') }}</h5>
      <McTable
        ref="alertTable"
        class="dashboard-alert-table"
        withCard
        stickyHeader
        searchable
        :fields="listAlertFields"
        :apiClient="apiClient"
        :rowClass="unreadAlerts"
        :hasCheckbox="false"
        @listRes="handleListRes"
      >
        <template #cell(alert)="{ item, value }">
          <div
            class="alert-wrapper"
            @click="openAlertPopup(item)"
          >
            <div class="d-flex flex-column">
              <span 
                v-if="hasRuleNameScope && item.alertRule"
                :data-me="`cdm-notif-list-${item.rawAlertId}`"
              >
                {{ item.alertRule.label }}
              </span>
              <span
                :class="{
                  'text-muted': hasRuleNameScope && item.alertRule,
                }"
                :data-me="`cdm-notif-list-${item.rawAlertId}`"
              >
                {{ getAlertDescription(value) }}
              </span>
            </div>
            <i
              v-b-tooltip.hover="tooltipSourceDeviceTypeContent"
              :class="{
                'alert-icon': true,
                'icon-outline_video': item?.sourceDeviceType === SOURCE_DEVICE_TYPE.CAM,
                'icon-outline_device': item?.sourceDeviceType === SOURCE_DEVICE_TYPE.GPS,
              }"
            />
          </div>
        </template>
        <template #cell(alertData)="{ item, value }">
          <span>
            {{ convertValue(value, item.alertCode) }}
          </span>
        </template>
        <template #cell(alertTrigger)="{ item, value }">
          <span>
            {{ convertValue(value, item.alertCode) }}
          </span>
        </template>
        <template #cell(createdOn)="{ value }">
          <template v-if="value">
            <div>{{ formatIndexDate(value).date }}</div>
            <div class="mc-datetime">
              {{ formatIndexDate(value).time }} 
            </div>
          </template>
          <template v-else> - </template>
        </template>
        <template #cell(address)="{ item, value }">
          <div
            v-if="value"
            class="address-wrapper"
          >
            <span
              class="address-text"
              @click="openGoogleMaps(item.latitude, item.longitude)"
            >
              {{ value }}
            </span>
            <span
              v-b-tooltip.hover
              class="address-copy-icon"
              :title="$t('global.field.copy')"
              @click="copyAddress(getLinkGoogleMaps(item.latitude, item.longitude))"
            >
              <i class="mc-fa icon-outline_copy"></i>
            </span>
          </div>
          <template v-else> - </template>
        </template>
      </McTable>
    </div>
  </div>
</template>
<script>
// Helper
import { Helper } from '@/mixin/helper'
import { Notification } from '@/mixin/notification'
import { formatIndexDate } from '@/helpers/tzDate'
import datePickerLang from '@/helpers/date-picker-lang'
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { openGoogleMaps, getLinkGoogleMaps } from '../libs/helper'
import msgpack from 'msgpack-lite'

// Components
import McTable from '@/components/McTable'
import McDatePicker from '@/components/McDatePicker'
import AlertWidget from '../components/AlertWidget'

// Libs
import { MILISECONDS_IN_A_DAY, SOURCE_DEVICE_TYPE, CDM_ALERT_CODES } from '../libs/constants'
import { listAlertFields } from '../libs/list-alert-fields'
import { EventBus } from '@/router/views/fleet-task/lib/event-bus'

// Services
import { UPSERT_RAW_ALERTS_ACTION } from '@/services/api/driverscore/score'
import { GET_CDM_RAW_ALERTS } from '@/services/api/cdm/alerts'
import { GET_PAYLOAD } from '@/services/api/auth'

moment.locale('id')

export default {
  name: 'CdmDashboard',
  components: {
    AlertWidget,
    McDatePicker,
    McTable,
  },
  mixins: [Helper, Notification, listAlertFields],
  data() {
    const {
      DEVICE_DISARMED,
      DOOR_OPEN,
      VIBRATION,
      ENGINE_TEMPERATURE,
      DOOR_CLOSE,
      MOTION_DETECTED,
      CAMERA_OFF,
      MOVEMENT,
      BUZZER_FAULTY,
      MOTION_FAULTY,
    } = CDM_ALERT_CODES

    return {
      SOURCE_DEVICE_TYPE,

      // CDM Alert Codes
      cdmAlertCodes: [
        DEVICE_DISARMED,
        DOOR_OPEN,
        VIBRATION,
        ENGINE_TEMPERATURE,
        DOOR_CLOSE,
        MOTION_DETECTED,
        CAMERA_OFF,
        MOVEMENT,
        BUZZER_FAULTY,
        MOTION_FAULTY,
      ],
      deviceCdmCodes: [
        MOTION_DETECTED,
        VIBRATION,
        DOOR_OPEN,
        DOOR_CLOSE,
        ENGINE_TEMPERATURE,
        MOVEMENT,
        DEVICE_DISARMED,
      ],
      cameraWarningCodes: [
        CAMERA_OFF,
        CAMERA_OFF,
        MOTION_FAULTY,
        BUZZER_FAULTY,
      ],

      periodDate: [moment().add(-1, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
      alertResponse: {},
      isLoading: false,
      isFirstLoad: true,
      isDatePickerLoading: false,

      addresses: {},

      // WebSocket debounce timer
      payloadWs: null,
      forceDisconnect: false,
      socket: null,

      wsDebounceTimer: null,
      wsIsWaitingForRefetch: false,
      wsMessageHandler: null,

      formatValue: [
        {
          alertCode: [
            DEVICE_DISARMED,
            DOOR_OPEN,
            VIBRATION,
            DOOR_CLOSE,
            CAMERA_OFF,
          ],
          format: [
            {
              type: 'value',
              symbol: '-',
            },
          ],
        },
        {
          alertCode: [ENGINE_TEMPERATURE],
          format: [
            {
              type: 'null-value',
              symbol: '-',
            },
            {
              type: 'suffix',
              symbol: 'global.unit.celcius',
            },
          ],
        },
      ],
    }
  },
  computed: {
    datePickerLang,
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    ...mapState('notification', [
      'activePopups',
    ]),
    lang() {
      return this.$i18n.locale
    },
    shortcuts() {
      return [
        {
          text: this.$t('global.time.today'),
          onClick: () => [new Date(), new Date()],
        },
        {
          text: this.$t('global.time.yesterday'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            endDate.setTime(endDate.getTime() - MILISECONDS_IN_A_DAY)
            startDate.setTime(startDate.getTime() - MILISECONDS_IN_A_DAY)

            startDate.setHours(0, 0, 0, 0)
            endDate.setHours(23, 59, 59, 999)

            return [startDate, endDate]
          }
        },
        {
          text: this.$t('global.time.last31Days'),
          onClick: () => {
            const date = new Date()
            date.setHours(0, 0, 0, 0)
            date.setTime(date.getTime() - MILISECONDS_IN_A_DAY * 31)
            return [date, new Date()]
          },
        },
        {
          text: this.$t('global.time.thisMonth'),
          onClick: () => {
            const date = new Date()
            date.setHours(0, 0, 0, 0)
            date.setTime(date.getTime() - MILISECONDS_IN_A_DAY * (date.getDate() - 1))
            return [date, new Date()]
          },
        },
      ].map((shortcut) => ({
        ...shortcut,
        value: shortcut.onClick,
      }))
    },
    tooltipSourceDeviceTypeContent() {
      return (item) => `${this.$t('notification.text.sourceDevice')}: ${
        item?.sourceDeviceType === SOURCE_DEVICE_TYPE.CAM ? this.$t('trackVideo.camera') : 'GPS'
      }`
    },
    unreadAlerts() {
      return (item, type) => {
        if (!item || type !== 'row') return
        // Alert is unread when isRead is explicitly false AND it's important/urgent
        if (
          item.isRead === false &&
          (item.isImportant || item.isUrgent)
        ) {
          return 'unread-notification'
        }
      }
    },
    deviceCdmAlerts() {
      const eachAlertTotal = this.alertResponse?.aggregate?.eachAlertTotal || []
      
      const alertMap = eachAlertTotal.reduce((map, alert) => {
        map[alert.alertCode] = alert.total
        return map
      }, {})
      
      const alertGroups = [
        { name: 'Motion Status', codes: [CDM_ALERT_CODES.MOTION_DETECTED], key: 'motionStatus' },
        { name: 'Getaran', codes: [CDM_ALERT_CODES.VIBRATION], key: 'vibration' },
        { name: 'Pintu', codes: [CDM_ALERT_CODES.DOOR_OPEN, CDM_ALERT_CODES.DOOR_CLOSE], key: 'door' },
        { name: 'Temperatur', codes: [CDM_ALERT_CODES.ENGINE_TEMPERATURE], key: 'temperature' },
        { name: 'Movement', codes: [CDM_ALERT_CODES.MOVEMENT], key: 'movement' },
        { name: 'Disconnect', codes: [CDM_ALERT_CODES.DEVICE_DISARMED], key: 'disconnect' },
      ]

      return alertGroups.map(group => {
        const count = group.codes.reduce((sum, code) => sum + (alertMap[code] || 0), 0)

        return {
          alertCode: group.key,
          name: group.name,
          count: count
        }
      })
    },
    cameraWarningAlerts() {
      const eachAlertTotal = this.alertResponse?.aggregate?.eachAlertTotal || []
      
      const alertMap = eachAlertTotal.reduce((map, alert) => {
        map[alert.alertCode] = alert.total
        return map
      }, {})
      
      const alertGroups = [
        { name: 'Broken', codes: [CDM_ALERT_CODES.CAMERA_OFF], key: 'broken' },
        { name: 'Disconnect', codes: [CDM_ALERT_CODES.CAMERA_OFF], key: 'disconnect' },
        { name: 'Motion Broken', codes: [CDM_ALERT_CODES.MOTION_FAULTY], key: 'motionBroken' },
        { name: 'Strobe Broken', codes: [CDM_ALERT_CODES.BUZZER_FAULTY], key: 'strobeBroken' },
      ]

      return alertGroups.map(group => {
        const count = group.codes.reduce((sum, code) => sum + (alertMap[code] || 0), 0)

        return {
          alertCode: group.key,
          name: group.name,
          count: count
        }
      })
    },
    apiClient() {
      return {
        get: async (filters) => {
          try {
            this.isLoading = true
            const formattedFilter = {
              vehicleCategory: ["CDM"],
              alertCode: [
                CDM_ALERT_CODES.DEVICE_DISARMED,
                CDM_ALERT_CODES.DOOR_OPEN,
                CDM_ALERT_CODES.VIBRATION,
                CDM_ALERT_CODES.ENGINE_TEMPERATURE,
                CDM_ALERT_CODES.DOOR_CLOSE,
                CDM_ALERT_CODES.MOTION_DETECTED,
                CDM_ALERT_CODES.MOVEMENT,
                CDM_ALERT_CODES.BUZZER_FAULTY,
                CDM_ALERT_CODES.MOTION_FAULTY,
              ],
              nestedFilter: [
                {
                  alertCode: CDM_ALERT_CODES.CAMERA_OFF,
                  alertFlag: true,
                  alertTrigger: 2
                }
              ],
              startDate: `${this.periodDate[0]}T00:00:00.000Z`,
              endDate: `${this.periodDate[1]}T23:59:59.999Z`,
              uncommentedOnly: true,
              includeEachAlertTotal: true,
              pagination: {
                show: filters.limit,
                page: filters.page,
              },
              search: filters.search,
              orderBy: JSON.stringify({ createdOn: 'desc' }),
            }

            const res = await this.$apollo.query({
              query: GET_CDM_RAW_ALERTS,
              fetchPolicy: 'no-cache',
              variables: {
                filter: formattedFilter,
              },
            })

            if (!res.data.rawAlerts.isSuccessful) {
              return
            }

            this.alertResponse = res.data.rawAlerts.rawAlerts
          
            const pagination = this.alertResponse?.pagination || {}
          
            return {
              metadata: {
                count: pagination.show || 0,
                page: pagination.page || 1,
                total_count: pagination.actualTotal || 0,
              },
              data: {
                paginated_result: this.alertResponse?.data || []
              },
            }
          } catch (error) {
            console.error('Error fetching raw alerts:', error)
            const pagination = this.alertResponse?.pagination || {}

            return {
              metadata: {
                count: pagination.show || 0,
                page: pagination.page || 1,
                total_count: pagination.actualTotal || 0,
              },
              data: {
                paginated_result: this.alertResponse?.data || [],
              },
            }
          } finally {
            this.isLoading = false
            this.isFirstLoad = false
          }
        }
      }
    },
    hasRuleNameScope() {
      return this.hasAccess('e4J-alert-rule-name-as-title:read')
    },
  },
  async mounted() {
    await this.setupWebSocket()
    EventBus.$on('refetch-cdm-alert-code', () => this.refetchCDMData(false))
  },
  beforeDestroy() {
    this.forceDisconnect = true
    if (this.socket) {
      this.socket.close()
    }

    if (this.wsDebounceTimer) {
      clearTimeout(this.wsDebounceTimer)
    }

    EventBus.$off('refetch-cdm-alert-code')
  },
  methods: {
    formatIndexDate,
    openGoogleMaps,
    getLinkGoogleMaps,    
    copyAddress(text) {
      navigator.clipboard.writeText(text)
      this.$mcToast.success(this.$t('global.action.copySuccess'))
    },
    getAlertDescription(alert) {
      return this.lang === 'id' ? alert?.descriptionIdn : alert?.descriptionEng
    },
    async setupWebSocket() {
      await this.getPayload()
      await this.startWebSocket()
    },
    async getPayload() {
      try {
        const res = await this.$apollo.query({
          query: GET_PAYLOAD,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              filterByType: ['Alert', 'AlertCenter', 'AlertCenterFeedback'],
            },
          },
        })

        this.payloadWs = res.data.getPayload.payload.payload
      } catch (error) {
        console.error(error)
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
      }
    },
    async startWebSocket() {
      if (!this.payloadWs) return
      const obj = {
        type: 'update_tags',
        data: this.payloadWs,
      }

      this.socket = new WebSocket(
        `${process.env.VUE_APP_WS_URL}/ws?token=${localStorage.getItem('wsToken')}`
      )

      this.socket.onopen = () => {
        this.socket.send(JSON.stringify(obj))
        this.isWsConnected = true
      }

      this.socket.onmessage = (message) => {
        const signal = JSON.parse(message.data)

        if (!Object.prototype.hasOwnProperty.call(signal, 'type')) return

        const companyId = this.getCompanyId()

        // Handle alert center feedback
        if (signal.type === `alert-center-feedback-${companyId}`) {
          const decodedData = this.decodeAlertWsData(signal.payload.data)

          decodedData.alertCode.map((code) => this.handleCDMWebSocketMessage(code))
        }
        // Handle alert center
        else if (signal.type === `alert-center-${signal?.payload?.vehicleId}` && companyId === signal?.payload?.companyId) {
          this.handleCDMWebSocketMessage(signal.payload.alertCode)
        }
        // Handle raw alerts
        else if (signal.type === `alert-${signal?.payload?.vehicleId}` && companyId === signal?.payload?.companyId) {
          this.handleCDMWebSocketMessage(signal.payload.alertCode)
        }
      }

      this.socket.onclose = async () => {
        if (this.forceDisconnect) return
        this.setupWebSocket()
      }
    },
    decodeAlertWsData(encodedData) {
      const decodedData = msgpack.decode(encodedData)
      
      return {
        alertCode: decodedData.ac,
      }
    },
    handleCDMWebSocketMessage(alertCode) {
      if (!this.cdmAlertCodes.includes(alertCode)) return

      if (this.wsIsWaitingForRefetch) return

      this.wsIsWaitingForRefetch = true

      if (this.wsDebounceTimer) {
        clearTimeout(this.wsDebounceTimer)
      }

      this.wsDebounceTimer = setTimeout(() => {
        this.refetchCDMData()
      }, 15000)
    },
    async refetchCDMData(silentUpdate = true, pageOverride = null) {
      try {
        if (!silentUpdate) {
          this.isDatePickerLoading = true
        }
        const table = this.$refs.alertTable
        if (table) {
          await table.getData({
            limit: table.limit,
            page: pageOverride !== null ? pageOverride : table.page,
            search: table.search || undefined,
            sorts:table.sorts.map((sort) => `${sort.key} ${sort.order}`),
          }, silentUpdate) // true = silent update, no loading state
        }
        
      } catch (error) {
        console.error('Failed to refresh data')
      } finally {
        this.wsIsWaitingForRefetch = false
        clearTimeout(this.wsDebounceTimer)
        this.wsDebounceTimer = null
        this.isDatePickerLoading = false
      }
    },
    closeCalendar() {
      this.$refs.periodPicker.cancel()
    },
    disabledDate(date, currentValue) {
      const MAX_DAYS_SELECTED = 32
      
      // Disable future dates
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (date > today) {
        return true
      }
      
      // When first date is selected, limit the range to 30 days
      if (currentValue && currentValue[0] && !currentValue[1]) {
        const startDate = new Date(currentValue[0])
        const minDate = new Date(startDate.getTime() - (MAX_DAYS_SELECTED - 1) * MILISECONDS_IN_A_DAY)
        const maxDate = new Date(startDate.getTime() + (MAX_DAYS_SELECTED - 1) * MILISECONDS_IN_A_DAY)
        return date < minDate || date > maxDate
      }
      
      return false
    },
    async readItem(item) {
      const input = {
        actionType: 'Read',
        data: null,
        rawAlertId: [item.rawAlertId],
        selectionMode: 'Selection',
      }
      const res = await this.$apollo.mutate({
        mutation: UPSERT_RAW_ALERTS_ACTION,
        variables: {
          input: input,
        },
      })
      if (!res.data.upsertRawAlertsAction.isSuccessful) {
        this.$mcToast.error(res.data.upsertRawAlertsAction.message)
        return
      }
      this.$mcToast.success(this.$t('notification.text.successRead'))
      const paginatedResult = this.alertResponse?.data
      if (paginatedResult) {
        paginatedResult.forEach((alert) => {
          if (alert.rawAlertId === item.rawAlertId) {
            alert.isRead = true
          }
        })
      }
    },
    async checkPopup(alertId) {
      const isOpened = this.activePopups.find(
        (popup) => String(popup.id) === String(alertId)
      )
      if (isOpened) {
        this.handleClosePopup(alertId)
        return false
      }

      return Boolean(await this.handleOpenPopup(alertId, { isCDMAlert: true }))
    },
    async openAlertPopup(item) {
      const alertId = item.rawAlertId ?? item.id
      const isOpened = this.activePopups.some(
        (popup) => String(popup.id) === String(alertId)
      )
      if (!isOpened && this.activePopups.length >= 9) {
        this.$mcToast.warning(this.$t('notification.text.limitNotification'), { timeout: 1000 })
        return
      }

      const isOpenedNow = await this.checkPopup(alertId)

      if (isOpenedNow && item.isRead !== true && (item.isImportant || item.isUrgent)) {
        await this.readItem(item)
      }
    },
    handleClosePopup(alertId) {
      this.$store.dispatch('notification/closePopup', alertId)
    },
    convertValue(value, alertCode) {
      let result = value

      if ([
        CDM_ALERT_CODES.MOVEMENT,
        CDM_ALERT_CODES.BUZZER_FAULTY,
        CDM_ALERT_CODES.MOTION_FAULTY,
        CDM_ALERT_CODES.MOTION_DETECTED,
      ].includes(alertCode)) {
        return value || '-'
      }
      
      const format = this.formatValue.find((item) => item.alertCode.includes(alertCode))
      if (format) {
        format.format.forEach((item) => {
          if (item.type == 'value') {
            result = item.symbol
          } else if (value == null || typeof value === 'undefined' || value === '') {
            if (item.type == 'null-value') {
              result = item.symbol
            }
          } else {
            if (item.type == 'suffix') {
              result = `${value} ${this.$t(item.symbol)}`
            }
          }
        })
      }
      
      return result || '-'
    },
    handleListRes(response) {
      const data = response?.data?.paginated_result || []
      const currentPage = response?.metadata?.page || 1
      
      // If data is empty but there's a previous page, refetch with page - 1
      if (data.length === 0 && currentPage > 1) {
        this.refetchCDMData(true, currentPage - 1)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.dashboard-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 0;

  // Header section with date picker
  .dashboard-header {
    display: flex;
    justify-content: flex-end;
  }

  .dashboard-period-picker {
    max-width: 216px;
    
    :deep(.mx-input) {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      cursor: pointer;
    }
  }

  // Alert widgets section
  .dashboard-alert-section {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .dashboard-widgets {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  // Alert table section
  .dashboard-alert-table {
    :deep(th), :deep(td) {
      padding: 10px;
    }
    
    .alert-wrapper{
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      cursor: pointer;
      color: $mep-primary-400;
      font-weight: 600;

      .alert-icon {
        color: $mep-primary-400;
        font-size: 20px;
      }
    }

    .address-wrapper {
      display: flex;
      justify-content: space-between;
      gap: 10px;

      .address-text {
        cursor: pointer;

        &:hover {
          color: $mep-primary-400;
          text-decoration: underline;
        }
      }

      .address-copy-icon {
        font-size: 20px;
        cursor: pointer;

        &:hover {
          color: $mep-primary-400;
        }
      }
    }
  }
}

// Unread notification row styling
:deep(.unread-notification) {
  td {
    background-color: #deeafc; // TODO: Consider moving to theme variables
  }
}
</style>
